import { Cage, CageWeek, Site } from 'views/playground/features/data-lab/types/cage.type';
import { FishGroup, Transfer } from 'views/playground/features/data-lab/types/fish-group.type';

export interface LabFixture {
  sites: Site[];
  cages: Cage[];
  fishGroups: FishGroup[];
  cageWeeks: CageWeek[];
}

// Deterministic mulberry32 generator. No `Math.random()` anywhere in this file so
// the fixture, and therefore every chart and KPI derived from it, is identical on
// every load. Exported so any future fixture reuses the same stream.
export const createRandom = (seed: number) => () => {
  seed = (seed + 0x6d2b79f5) | 0;
  let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const FIXTURE_SEED = 20260904;
const WEEK_COUNT = 26;

const SITE_CONFIG: { id: string; name: string; cageCount: number }[] = [
  { id: 'SITE-A', name: 'Northaven', cageCount: 6 },
  { id: 'SITE-B', name: 'Skarsvik', cageCount: 5 },
  { id: 'SITE-C', name: 'Baie Verte', cageCount: 7 },
  { id: 'SITE-D', name: 'Fjordholm', cageCount: 4 },
  { id: 'SITE-E', name: 'Sundlight Bay', cageCount: 8 },
  { id: 'SITE-F', name: 'Kaldvik', cageCount: 6 },
];

const MORTALITY_SPIKE_CAGE_ID = 'SITE-B-C2';
const MORTALITY_SPIKE_WEEK = 14;
const MORTALITY_SPIKE_MULTIPLIER = 6;

// Raised background mortality for three cages, so threshold-colored charts show every status band.
const CHRONIC_MORTALITY_MULTIPLIERS: Record<string, number> = { 'SITE-C-C3': 2.2, 'SITE-E-C5': 4, 'SITE-F-C2': 7 };

const FCR_DRIFT_CAGE_ID = 'SITE-D-C1';
const FCR_DRIFT_START_WEEK = 8;
const FCR_DRIFT_PER_WEEK = 0.03;

const randomRange = (rng: () => number, min: number, max: number) => min + rng() * (max - min);

const buildSitesAndCages = (rng: () => number): { sites: Site[]; cages: Cage[] } => {
  const sites: Site[] = [];
  const cages: Cage[] = [];

  SITE_CONFIG.forEach(({ id: siteId, name, cageCount }) => {
    const cageIds: string[] = [];

    for (let cageNumber = 1; cageNumber <= cageCount; cageNumber += 1) {
      const cageId = `${siteId}-C${cageNumber}`;
      cageIds.push(cageId);
      cages.push({
        id: cageId,
        siteId,
        name: `Cage ${cageNumber}`,
        capacityKg: Math.round(randomRange(rng, 40000, 90000)),
      });
    }

    sites.push({ id: siteId, name, cageIds });
  });

  return { sites, cages };
};

const buildCageWeeks = (cages: Cage[], rng: () => number): CageWeek[] => {
  const cageWeeks: CageWeek[] = [];

  cages.forEach((cage) => {
    const initialWeightG = randomRange(rng, 120, 180);
    const dailySgrPercent = randomRange(rng, 1.1, 1.6);
    const baselineFcr = randomRange(rng, 1.1, 1.3);
    const initialPopulation = Math.round(cage.capacityKg / 4.5);
    const seasonPhase = randomRange(rng, 0, Math.PI * 2);

    let population = initialPopulation;
    let averageWeightG = initialWeightG;
    let previousBiomassKg = (population * averageWeightG) / 1000;

    for (let week = 1; week <= WEEK_COUNT; week += 1) {
      const nextWeightG = averageWeightG * Math.exp((dailySgrPercent / 100) * 7 * randomRange(rng, 0.94, 1.06));

      let mortality = Math.round(population * randomRange(rng, 0.0003, 0.0008) * (CHRONIC_MORTALITY_MULTIPLIERS[cage.id] ?? 1));
      if (cage.id === MORTALITY_SPIKE_CAGE_ID && week === MORTALITY_SPIKE_WEEK) {
        mortality = Math.round(mortality * MORTALITY_SPIKE_MULTIPLIER);
      }
      population = Math.max(0, population - mortality);

      const biomassKg = (population * nextWeightG) / 1000;
      const biomassGainKg = Math.max(0, biomassKg - previousBiomassKg);

      let feedKg = biomassGainKg * baselineFcr + biomassKg * 0.01 * randomRange(rng, 0.9, 1.1);
      if (cage.id === FCR_DRIFT_CAGE_ID && week >= FCR_DRIFT_START_WEEK) {
        const weeksIntoDrift = week - FCR_DRIFT_START_WEEK;
        feedKg *= 1 + FCR_DRIFT_PER_WEEK * weeksIntoDrift;
      }

      const temperatureC = 8 + 6 * Math.sin((2 * Math.PI * week) / 52 + seasonPhase) + randomRange(rng, -0.4, 0.4);

      cageWeeks.push({
        cageId: cage.id,
        week,
        biomassKg: Math.round(biomassKg),
        feedKg: Math.round(feedKg),
        mortality,
        averageWeightG: Math.round(nextWeightG),
        temperatureC: Math.round(temperatureC * 10) / 10,
      });

      averageWeightG = nextWeightG;
      previousBiomassKg = biomassKg;
    }
  });

  return cageWeeks;
};

const buildFishGroups = (cages: Cage[], rng: () => number): FishGroup[] => {
  const groupCount = 12;
  const fishGroups: FishGroup[] = [];

  for (let index = 0; index < groupCount; index += 1) {
    const groupId = `FG-${String(index + 1).padStart(2, '0')}`;
    const stockingCage = cages[Math.floor(rng() * cages.length)];
    const transferCount = Math.floor(randomRange(rng, 1, 4));

    const transfers: Transfer[] = [
      {
        fromCageId: null,
        toCageId: stockingCage.id,
        week: 1,
        biomassKg: Math.round(randomRange(rng, 2000, 6000)),
      },
    ];

    let currentCage = stockingCage;
    let currentBiomassKg = transfers[0].biomassKg;
    let currentWeek = 1;

    for (let transferIndex = 0; transferIndex < transferCount; transferIndex += 1) {
      currentWeek += Math.round(randomRange(rng, 4, 8));
      if (currentWeek >= WEEK_COUNT) break;

      const nextCage = cages[Math.floor(rng() * cages.length)];
      currentBiomassKg = Math.round(currentBiomassKg * randomRange(rng, 1.3, 1.9));

      transfers.push({
        fromCageId: currentCage.id,
        toCageId: nextCage.id,
        week: currentWeek,
        biomassKg: currentBiomassKg,
      });

      currentCage = nextCage;
    }

    fishGroups.push({
      id: groupId,
      name: `Fish Group ${index + 1}`,
      transfers,
    });
  }

  return fishGroups;
};

const buildFixture = (): LabFixture => {
  const rng = createRandom(FIXTURE_SEED);
  const { sites, cages } = buildSitesAndCages(rng);
  const cageWeeks = buildCageWeeks(cages, rng);
  const fishGroups = buildFishGroups(cages, rng);

  return { sites, cages, fishGroups, cageWeeks };
};

// Generated once at module scope. Screens import this finished object and derive
// chart shapes from it with the pure helpers in `utils/derive-chart-data.ts`.
export const LAB_FIXTURE: LabFixture = buildFixture();
